import React, { createContext, useContext, useState, useEffect } from 'react';

// Mr. Mohamed Al-Atfy Platform - Auth Context
// LOCAL DEV: Uses localhost; PRODUCTION: Uses live endpoint
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const defaultAuthValue = {
  user: null,
  profile: null,
  token: null,
  loading: true,
  signOut: async () => { },
  login: async () => ({ success: false, error: 'Auth not initialized' }),
  register: async () => ({ success: false, error: 'Auth not initialized' }),
  signIn: async () => ({ success: false, error: 'Auth not initialized' }),
  signUp: async () => ({ success: false, error: 'Auth not initialized' }),
};

const AuthContext = createContext(defaultAuthValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context ?? defaultAuthValue;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Check Auth on Load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUserId = localStorage.getItem('userId');

        if (storedToken && storedUserId) {
          setToken(storedToken);
          setUser({ id: storedUserId });

          try {
            const res = await fetch(`${API_BASE_URL}/profiles/${storedUserId}`);
            const data = await res.json();
            if (res.ok && data.success && data.data) {
              setProfile(data.data);
            } else {
              setProfile({ accountStatus: 'active', role: 'student', firstName: 'طالب' });
            }
          } catch (e) {
            setProfile({ accountStatus: 'active', role: 'student', firstName: 'طالب' });
          }
        } else {
          setUser(null);
          setProfile(null);
          setToken(null);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 2. Login
  const login = async (email, password, deviceId) => {
    try {
      const safeEmail = (email || '').trim().toLowerCase();
      // Extract phone from email for admin bypass check
      const phoneMatch = safeEmail.match(/^(\d+)@/);
      const phone = phoneMatch ? phoneMatch[1] : null;

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: safeEmail, password, device_id: deviceId || null })
      });

      const rawText = await response.text();
      let data = {};
      try {
        data = JSON.parse(rawText);
      } catch {
        console.error('Login: non-JSON response from server:', rawText);
        return { success: false, error: 'مسار تسجيل الدخول غير متاح على السيرفر', status: response.status };
      }

      if (!response.ok) {
        return { success: false, error: data.error || 'فشل تسجيل الدخول', status: response.status };
      }

      const userId = data.userId || (data.user && data.user.id);
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', userId);

      setToken(data.token);
      setUser({ id: userId });

      let finalProfile = data.user;
      if (!finalProfile) {
        try {
          const profRes = await fetch(`${API_BASE_URL}/profiles/${userId}`);
          const profData = await profRes.json();
          finalProfile = profData.data || { accountStatus: 'active', role: 'student' };
        } catch (e) {
          finalProfile = { accountStatus: 'active', role: 'student' };
        }
      }

      // ADMIN BYPASS FOR LOCAL TESTING: Phone 01015397643 gets admin role
      if (phone === '01015397643') {
        finalProfile = { ...finalProfile, role: 'admin', accountStatus: 'active' };
        console.log('✅ Admin bypass activated for testing:', phone);
      }

      setProfile(finalProfile);

      return { success: true, user: { id: userId }, profile: finalProfile };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'مشكلة في الاتصال بالخادم' };
    }
  };

  // 3. Register
  const register = async (email, password, fullName, profileData) => {
    try {
      // تم التعديل للينك D1
      const response = await fetch(`${API_BASE_URL}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          firstName: profileData.firstName || '',
          middleName: profileData.middleName || '',
          lastName: profileData.lastName || '',
          phone: profileData.phone || '',
          grade: profileData.grade || '',
          governorate: profileData.governorate || '',
          school: profileData.school || '',
          father_phone: profileData.father_phone || '',
          mother_phone: profileData.mother_phone || '',
          bio: profileData.bio || '',
          study_branch: profileData.study_branch || 'scientific',
          device_id: profileData.device_id || null,
        })
      });

      const rawText = await response.text();
      let data = {};
      try {
        data = JSON.parse(rawText);
      } catch {
        console.error('Register: non-JSON response from server:', rawText);
        return { success: false, error: 'مسار إنشاء الحساب غير متاح على السيرفر.' };
      }

      if (!response.ok) {
        return { success: false, error: data.error || 'فشل إنشاء الحساب' };
      }

      return { success: true, userId: data.userId };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: 'مشكلة في الاتصال بالخادم' };
    }
  };

  // 4. Sign Out
  const signOut = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setToken(null);
      setUser(null);
      setProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (phone, password, deviceId) => {
    const cleanPhone = typeof phone === 'string' ? phone.trim().replace(/\s/g, '') : phone;
    const dummyEmail = `${cleanPhone}@sabeel.com`;
    const res = await login(dummyEmail, typeof password === 'string' ? password.trim() : password, deviceId);
    if (!res?.success && res?.status === 401) {
      const err = new Error('ACCOUNT_NOT_FOUND');
      err.code = 'ACCOUNT_NOT_FOUND';
      err.status = 401;
      err.userMessage =
        'عفواً، الحساب غير موجود. يرجى إنشاء حساب جديد للدخول.';
      throw err;
    }
    return res;
  };

  const signUp = async (phone, password, userData) => {
    const cleanPhone = typeof phone === 'string' ? phone.trim().replace(/\s/g, '') : phone;
    const dummyEmail = `${cleanPhone}@sabeel.com`;
    const fullName = `${userData.firstName || ''} ${userData.middleName || ''} ${userData.lastName || ''}`.trim();
    return register(dummyEmail, password, fullName, {
      ...userData,
      phone: cleanPhone,
    });
  };

  const value = {
    user,
    profile,
    token,
    loading,
    login,
    register,
    signIn,
    signUp,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};